import Stack from '@mui/material/Stack'
import type { TFunction } from 'i18next'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import BidStatusChip from '@/features/bidding/components/BidStatusChip'
import type { BidOverviewSummaryCell } from '@/features/bidding/types/bidOverviewSummary'
import { convertToRoundedMw } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import type { CapacityStatusChip } from '@/features/bidding/utils/model/getCapacityStatusChips'
import { getCapacityStatusChips } from '@/features/bidding/utils/model/getCapacityStatusChips'

type CapacityBidStatusChipsProps = {
  cell: BidOverviewSummaryCell
}

const formatVolume = (volume: number | null | undefined, unit: string): string =>
  volume != null ? `${convertToRoundedMw(volume)} ${unit}` : '-'

const chipLabel = (chip: CapacityStatusChip, unit: string, t: TFunction<'translation'>): string => {
  if (chip.percentage != null) return `${formatVolume(chip.volume, unit)} · ${Math.round(chip.percentage)}%`
  if (chip.volume != null) return formatVolume(chip.volume, unit)
  return t(`bidding.status.${chip.status}`)
}

function CapacityBidStatusChips({ cell }: Readonly<CapacityBidStatusChipsProps>): ReactElement {
  const { t } = useTranslation()
  const chips = getCapacityStatusChips(cell)

  return (
    <Stack
      sx={{ flexDirection: 'row', alignItems: 'center', height: '100%', width: 'fit-content', position: 'relative' }}
    >
      {chips.map((chip, index) => (
        <BidStatusChip
          key={chip.status}
          isFirst={index === 0}
          isLast={index === chips.length - 1}
          label={index === 0 ? chipLabel(chip, cell.unit, t) : undefined}
          status={chip.status}
          sx={{ zIndex: chips.length - index, overflow: 'hidden' }}
        />
      ))}
    </Stack>
  )
}

export default CapacityBidStatusChips
